
import { memo, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Markup, { bodyClass } from "./usermarkup";
import { usePage } from "../hooks/usePage";

import {
  getOfficers,
  createOfficer,
  updateOfficer,
  deleteOfficer,
  clearUserError,
} from "../store/slices/user";

import { getSubDivisions } from "../store/slices/subdivision";
import { getCircles } from "../store/slices/circle";
import { getStations } from "../store/slices/station";
import { getBooths } from "../store/slices/booth";

const PageMarkup = memo(Markup);

const DESIGNATIONS = [
  "SP",
  "ADDITIONAL_SP",
  "DSP",
  "INSPECTOR",
  "SI",
  "CSI",
  "ASI",
  "HEAD_CONSTABLE",
  "POLICE_CONSTABLE",
  "HOME_GUARD",
  "CAPF",
  "OTHER",
];

const DUTY_WINGS = [
  "POLLING_STATION",
  "ROUTE_MOBILE",
  "CHECK_POST",
  "STRIKING_FORCE",
  "FLYING_SQUAD",
  "STATIC_SURVEILLANCE",
  "VIDEO_SURVEILLANCE",
  "COORDINATION_TEAM",
  "GAS_PARTY",
  "OTHER",
];

const initialForm = {
  username: "",
  displayName: "",
  phone: "",
  password: "",
  badgeNo: "",
  designation: "POLICE_CONSTABLE",
  designationName: "",
  dutyWing: "POLLING_STATION",
  subDivisionId: "",
  circleId: "",
  stationId: "",
  sectorId: "",
  boothId: "",
  specialDutyId: "",
  superiorOfficerId: "",
  vehicleNo: "",
  shift: "",
  active: true,
};

const getId = (value) => {
  if (!value) return "";
  return typeof value === "object" ? value._id || "" : value;
};

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.message || error?.payload || fallback;
};

const normalizeOfficer = (officer) => ({
  ...initialForm,
  ...officer,
  password: "",
  subDivisionId: getId(officer?.subDivisionId),
  circleId: getId(officer?.circleId),
  stationId: getId(officer?.stationId),
  sectorId: getId(officer?.sectorId),
  boothId: getId(officer?.boothId),
  specialDutyId: getId(officer?.specialDutyId),
  superiorOfficerId: getId(officer?.superiorOfficerId),
  active: officer?.active ?? true,
});

export default function User() {
  usePage({
    title: "Officer Management - Nellore Police Dashboard",
    bodyClass,
  });

  const dispatch = useDispatch();

  const {
    officers = [],
    loading,
    createLoading,
    updateLoading,
    deleteLoading,
    error,
    pagination,
  } = useSelector((state) => state.user);

  const { subdivisions = [] } = useSelector(
    (state) => state.subdivision
  );

  const { circles = [] } = useSelector(
    (state) => state.circle
  );

  const { stations = [] } = useSelector(
    (state) => state.station
  );

  const { booths = [] } = useSelector(
    (state) => state.booth
  );
  const [search, setSearch] = useState("");
  const [designationFilter, setDesignationFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [viewMode, setViewMode] = useState("grid");

  const [showModal, setShowModal] = useState(false);
  const [editingOfficerId, setEditingOfficerId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formError, setFormError] = useState("");

  const isSaving = createLoading || updateLoading;

  // --------------------------------------------------
  // LOAD DATA
  // --------------------------------------------------

  const loadData = () => {
    dispatch(getOfficers({ page: 1, limit: 1000 }));
    dispatch(getSubDivisions({ page: 1, limit: 1000 }));
    dispatch(getCircles({ page: 1, limit: 1000 }));
    dispatch(getStations({ page: 1, limit: 1000 }));
    dispatch(getBooths({ page: 1, limit: 1000 }));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // --------------------------------------------------
  // FILTER OFFICERS
  // --------------------------------------------------

  const filteredOfficers = useMemo(() => {
    return officers.filter((officer) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        [
          officer.displayName,
          officer.username,
          officer.officerId,
          officer.badgeNo,
          officer.phone,
          officer.designation,
          officer.designationName,
        ].some((value) =>
          String(value || "").toLowerCase().includes(searchText)
        );

      const matchesDesignation =
        designationFilter === "ALL" ||
        officer.designation === designationFilter;

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && officer.active === true) ||
        (statusFilter === "INACTIVE" && officer.active === false);

      return (
        matchesSearch &&
        matchesDesignation &&
        matchesStatus
      );
    });
  }, [officers, search, designationFilter, statusFilter]);

  // --------------------------------------------------
  // STATISTICS
  // --------------------------------------------------

  const statistics = useMemo(
    () => ({
      total: officers.length,
      active: officers.filter((item) => item.active === true).length,
      inactive: officers.filter((item) => item.active === false).length,
      online: officers.filter((item) => item.isOnline === true).length,
    }),
    [officers]
  );

  // --------------------------------------------------
  // MODAL
  // --------------------------------------------------

  const openAddModal = () => {
    dispatch(clearUserError());
    setEditingOfficerId(null);
    setForm(initialForm);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (officer) => {
    dispatch(clearUserError());

    setEditingOfficerId(officer._id);
    setForm(normalizeOfficer(officer));
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSaving) return;

    setShowModal(false);
    setEditingOfficerId(null);
    setForm(initialForm);
    setFormError("");
  };

  // --------------------------------------------------
  // FORM HANDLERS
  // --------------------------------------------------

  const handleFormChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleSubdivisionChange = (subDivisionId) => {
    setForm((previous) => ({
      ...previous,
      subDivisionId,
      circleId: "",
      stationId: "",
    }));
  };

  const handleCircleChange = (circleId) => {
    setForm((previous) => ({
      ...previous,
      circleId,
      stationId: "",
    }));
  };

  const handleDesignationChange = (designation) => {
    setForm((previous) => ({
      ...previous,
      designation,
      subDivisionId: "",
      circleId: "",
      stationId: "",
    }));
  };

  // --------------------------------------------------
  // CREATE / UPDATE
  // --------------------------------------------------

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setFormError("");

    if (
      !form.username.trim() ||
      !form.displayName.trim() ||
      !form.phone.trim() ||
      !form.designation
    ) {
      setFormError("Please fill all required fields.");
      return;
    }

    if (!editingOfficerId && !form.password.trim()) {
      setFormError("Password is required when creating an officer.");
      return;
    }

    if (form.designation === "DSP" && !form.subDivisionId) {
      setFormError("Please select a Sub-Division for the DSP.");
      return;
    }

    if (
      form.designation === "INSPECTOR" &&
      (!form.subDivisionId || !form.circleId)
    ) {
      setFormError("Please select a Sub-Division and Circle for the Inspector.");
      return;
    }

    const payload = {
      username: form.username.trim().toLowerCase(),
      displayName: form.displayName.trim(),
      phone: form.phone.trim(),
      badgeNo: form.badgeNo.trim() || null,
      designation: form.designation,
      designationName: form.designationName.trim() || null,
      dutyWing: form.dutyWing,

      subDivisionId: form.subDivisionId || null,
      circleId: form.circleId || null,
      stationId: form.stationId || null,
      sectorId: form.sectorId.trim() || null,
      boothId: form.boothId.trim() || null,
      specialDutyId: form.specialDutyId.trim() || null,
      superiorOfficerId: form.superiorOfficerId || null,

      vehicleNo: form.vehicleNo.trim() || null,
      shift: form.shift.trim() || null,
      active: form.active,
    };

    if (form.password.trim()) {
      payload.password = form.password;
    }

    try {
      if (editingOfficerId) {
        await dispatch(
          updateOfficer({
            id: editingOfficerId,
            payload,
          })
        ).unwrap();
      } else {
        await dispatch(createOfficer(payload)).unwrap();
      }

      setShowModal(false);
      setEditingOfficerId(null);
      setForm(initialForm);
      setFormError("");

      dispatch(getOfficers({ page: 1, limit: 1000 }));
    } catch (err) {
      setFormError(
        getErrorMessage(err, "Failed to save officer.")
      );
    }
  };

  // --------------------------------------------------
  // DELETE
  // --------------------------------------------------

  const handleDelete = async (officer) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${officer.displayName || officer.username}?`
    );

    if (!confirmed) return;

    try {
      await dispatch(deleteOfficer(officer._id)).unwrap();
      dispatch(getOfficers({ page: 1, limit: 1000 }));
    } catch (err) {
      window.alert(getErrorMessage(err, "Failed to delete officer."));
    }
  };

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <PageMarkup
      officers={filteredOfficers}
      totalOfficers={officers.length}
      pagination={pagination}
      statistics={statistics}
      loading={loading}
      createLoading={createLoading}
      updateLoading={updateLoading}
      deleteLoading={deleteLoading}
      error={error}
      search={search}
      setSearch={setSearch}
      designationFilter={designationFilter}
      setDesignationFilter={setDesignationFilter}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      viewMode={viewMode}
      setViewMode={setViewMode}
      showModal={showModal}
      editingOfficerId={editingOfficerId}
      form={form}
      formError={formError}
      subdivisions={subdivisions}
      circles={circles}
      stations={stations}
      officersList={officers}
      designations={DESIGNATIONS}
      dutyWings={DUTY_WINGS}
      onAddOfficer={openAddModal}
      onEditOfficer={openEditModal}
      onDeleteOfficer={handleDelete}
      onCloseModal={closeModal}
      onFormChange={handleFormChange}
      onSubdivisionChange={handleSubdivisionChange}
      onCircleChange={handleCircleChange}
      onDesignationChange={handleDesignationChange}
      onSubmit={handleSubmit}
      booths={booths}
    />
  );
}
